import {
  Button,
  Combobox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Option,
} from "@fluentui/react-components";
import {
  AddRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
} from "@fluentui/react-icons";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useAddRoleDialogStyles } from "./AddRoleDialog.styles";
import { IAddRoleDialogProps } from "./AddRoleDialog.types";
import { UNASSIGNED_LABEL } from "./WorkspacePeople.constants";

export const AddRoleDialog = ({
  isOpen,
  members,
  onDismiss,
  onConfirm,
}: IAddRoleDialogProps): JSX.Element => {
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const styles = useAddRoleDialogStyles();

  const [roleName, setRoleName] = useState("");
  const [responsibilities, setResponsibilities] = useState<string[]>([""]);
  const [assignedMember, setAssignedMember] = useState("");
  const [roleNameError, setRoleNameError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setRoleName("");
    setResponsibilities([""]);
    setAssignedMember("");
    setRoleNameError("");
  }, [isOpen]);

  const handleResponsibilityChange = (index: number, value: string) => {
    setResponsibilities((prev) =>
      prev.map((responsibility, i) => (i === index ? value : responsibility)),
    );
  };

  const handleResponsibilityDelete = (index: number) => {
    setResponsibilities((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddResponsibility = () => {
    setResponsibilities((prev) => [...prev, ""]);
  };

  const handleConfirm = () => {
    if (!roleName.trim()) {
      setRoleNameError("Role name is required");
      return;
    }
    onConfirm({
      id: crypto.randomUUID(),
      name: roleName.trim(),
      responsibilities: responsibilities.map((r) => r.trim()).filter(Boolean),
      assignedMember: assignedMember.trim() || UNASSIGNED_LABEL,
      isDefault: false,
    });
  };

  const handleDismiss = () => {
    onDismiss();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) handleDismiss();
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular aria-hidden="true" />}
                aria-label="Close dialog"
                onClick={handleDismiss}
              />
            }
          >
            Add team member role
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              <Field
                label="Role name"
                required
                validationState={roleNameError ? "error" : "none"}
                validationMessage={roleNameError || undefined}
              >
                <Input
                  value={roleName}
                  onChange={(_e, data) => {
                    setRoleName(data.value);
                    if (roleNameError) setRoleNameError("");
                  }}
                  placeholder="e.g. Delivery Manager"
                  autoFocus
                  aria-label="Role name"
                />
              </Field>
              <Field label="Assigned to (optional)">
                <Combobox
                  freeform
                  value={assignedMember}
                  placeholder="Search or type a name"
                  onChange={(e) => setAssignedMember(e.target.value)}
                  onOptionSelect={(_, data) =>
                    setAssignedMember(data.optionText ?? "")
                  }
                  aria-label="Assigned member"
                >
                  {[...members]
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((person) => (
                      <Option key={person.userId} value={person.displayName}>
                        {person.displayName}
                      </Option>
                    ))}
                </Combobox>
              </Field>
              <Field label="Responsibilities (optional)">
                <div className={styles.responsibilitiesList}>
                  {responsibilities.map((responsibility, index) => (
                    <div key={index} className={styles.responsibilityRow}>
                      <Input
                        value={responsibility}
                        onChange={(_e, data) =>
                          handleResponsibilityChange(index, data.value)
                        }
                        placeholder="e.g. Coordinate sprint ceremonies"
                        aria-label={`Responsibility ${index + 1}`}
                      />
                      {responsibilities.length > 1 && (
                        <Button
                          appearance="subtle"
                          icon={<DeleteRegular aria-hidden="true" />}
                          aria-label={`Remove responsibility ${index + 1}`}
                          onClick={() => handleResponsibilityDelete(index)}
                        />
                      )}
                    </div>
                  ))}
                  <Button
                    appearance="transparent"
                    icon={<AddRegular aria-hidden="true" />}
                    onClick={handleAddResponsibility}
                    className={styles.addResponsibilityButton}
                  >
                    Add responsibility
                  </Button>
                </div>
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip="Add role"
            dimmed={!roleName.trim()}
            onClick={handleConfirm}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
